import { Form, Button, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useIntl } from 'react-intl';
import Config from '../../config/Config';
import '../../css/DppForm.css';

// Validation schema
const schema = z.object({
  TCFCalculationMethod: z.enum(['EN 16258'], {
    required_error: 'Calculation method is required',
  }),
  TCFCO2eq: z
    .string()
    .nonempty({ message: 'CO2 equivalent is required' })
    .refine((val) => !isNaN(Number(val)) && Number(val) >= 0, {
      message: 'Please enter a non-negative number',
    })
    .transform((val) => Number(val)),
  TCFReferenceValueForCalculation: z.enum(['g', 'kg', 't', 'ml', 'l', 'cbm', 'qm', 'piece'], {
    required_error: 'Reference value is required',
  }),
  TCFQuantityOfMeasureForCalculation: z
    .string()
    .nonempty({ message: 'Quantity of measure is required' })
    .refine((val) => !isNaN(Number(val)) && Number(val) >= 0, {
      message: 'Please enter a non-negative number',
    })
    .transform((val) => Number(val)),
  TCFProcessesForGreenhouseGasEmissionInATransportService: z.enum(
    ['WTT - Well-to-Tank', 'TTW - Tank-to-Wheel', 'WTW - Well-to-Wheel'],
    {
      required_error: 'Process method is required',
    }
  ),
  TCFGoodsTransportAddressTakeover: z.object({
    Street: z.string().min(1, { message: 'Street is required' }),
    HouseNumber: z.string().min(1, { message: 'House number is required' }),
    ZipCode: z.string().min(1, { message: 'Zip code is required' }),
    CityTown: z.string().min(1, { message: 'City/town is required' }),
    Country: z.string().min(1, { message: 'Country is required' }).max(2, { message: 'Country must be written as 2-letter national code' }),
    Latitude: z
      .string()
      .optional()
      .refine((val) => !isNaN(Number(val)) && Number(val) >= -90 && Number(val) <= 90, {
        message: 'Latitude must be between -90 and 90',
      })
      .transform((val) => Number(val)),
    Longitude: z
      .string()
      .optional()
      .refine((val) => !isNaN(Number(val)) && Number(val) >= -180 && Number(val) <= 180, {
        message: 'Longitude must be between -180 and 180',
      })
      .transform((val) => Number(val)),
  }),
  TCFGoodsTransportAddressHandover: z.object({
    Street: z.string().min(1, { message: 'Street is required' }),
    HouseNumber: z.string().min(1, { message: 'House number is required' }),
    ZipCode: z.string().min(1, { message: 'Zip code is required' }),
    CityTown: z.string().min(1, { message: 'City/town is required' }),
    Country: z.string().min(1, { message: 'Country is required' }).max(2, { message: 'Country must be written as 2-letter national code' }),
    Latitude: z
      .string()
      .optional()
      .refine((val) => !isNaN(Number(val)) && Number(val) >= -90 && Number(val) <= 90, {
        message: 'Latitude must be between -90 and 90',
      })
      .transform((val) => Number(val)),
    Longitude: z
      .string()
      .optional()
      .refine((val) => !isNaN(Number(val)) && Number(val) >= -180 && Number(val) <= 180, {
        message: 'Longitude must be between -180 and 180',
      })
      .transform((val) => Number(val)),
  }),
});

const CO2ConsumptionsForm = ({
  showButtons = true,
  onSave,
  onCancel,
  isModal = false,
}) => {
  const intl = useIntl();
  const isDemo = Config.isDemo === 'true';
  const noAccessMessage = intl.formatMessage({ id: 'no_access_message', defaultMessage: 'Not authorized for this action' });
  const companyAddress = Config.company.address;
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    reset,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      TCFCalculationMethod: 'EN 16258',
      TCFCO2eq: '',
      TCFReferenceValueForCalculation: 'piece',
      TCFQuantityOfMeasureForCalculation: '',
      TCFProcessesForGreenhouseGasEmissionInATransportService: 'WTT - Well-to-Tank',
      TCFGoodsTransportAddressHandover: { // Handover is the address where transfer starts
        Street: companyAddress.street,
        HouseNumber: companyAddress.houseNumber,
        ZipCode: companyAddress.zipCode,
        CityTown: companyAddress.city,
        Country: companyAddress.country,
        Latitude: companyAddress.latitude,
        Longitude: companyAddress.longitude,
      },
      TCFGoodsTransportAddressTakeover: { // Takeover is the address where transfer ends
        Street: '',
        HouseNumber: '',
        ZipCode: '',
        CityTown: '',
        Country: '',
        Latitude: '',
        Longitude: '',
      },
    },
    mode: 'onTouched',
  });


  const handleCancel = () => {
    reset();
    onCancel?.();
  };

  const handleSave = (data) => {
    // Transform data for backend
    const cleanedData = {
      ...data,
      TCFCO2eq: data.TCFCO2eq ? Number(data.TCFCO2eq) : undefined,
      TCFQuantityOfMeasureForCalculation: data.TCFQuantityOfMeasureForCalculation
        ? Number(data.TCFQuantityOfMeasureForCalculation)
        : undefined,
      TCFGoodsTransportAddressTakeover: {
        ...data.TCFGoodsTransportAddressTakeover,
        Latitude: data.TCFGoodsTransportAddressTakeover.Latitude
          ? Number(data.TCFGoodsTransportAddressTakeover.Latitude)
          : undefined,
        Longitude: data.TCFGoodsTransportAddressTakeover.Longitude
          ? Number(data.TCFGoodsTransportAddressTakeover.Longitude)
          : undefined,
      },
      TCFGoodsTransportAddressHandover: {
        ...data.TCFGoodsTransportAddressHandover,
        Latitude: data.TCFGoodsTransportAddressHandover.Latitude
          ? Number(data.TCFGoodsTransportAddressHandover.Latitude)
          : undefined,
        Longitude: data.TCFGoodsTransportAddressHandover.Longitude
          ? Number(data.TCFGoodsTransportAddressHandover.Longitude)
          : undefined,
      },
    };
    onSave?.(cleanedData);
    if (isModal) reset();
  };

  return (
    <Form onSubmit={handleSubmit(handleSave)} noValidate className="dpp-form">
      {/* Main Properties */}
      <Row>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="TCFCalculationMethod">
            <Form.Label>{intl.formatMessage({ id: 'calculation_method' })}</Form.Label>
            <Form.Select
              {...register('TCFCalculationMethod')}
              isInvalid={!!errors.TCFCalculationMethod}
              className="dpp-form-control"
            >
              <option value="EN 16258">EN 16258</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.TCFCalculationMethod?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="TCFCO2eq">
            <Form.Label>
              {intl.formatMessage({ id: 'co2_equivalent' })}
              <span className="text-red-600 ml-1" aria-hidden="true">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              {...register('TCFCO2eq')}
              isInvalid={!!errors.TCFCO2eq}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.TCFCO2eq?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="TCFReferenceValueForCalculation">
            <Form.Label>{intl.formatMessage({ id: 'reference_value' })}</Form.Label>
            <Form.Select
              {...register('TCFReferenceValueForCalculation')}
              isInvalid={!!errors.TCFReferenceValueForCalculation}
              className="dpp-form-control"
            >
              <option value="g">g</option>
              <option value="kg">kg</option>
              <option value="t">t</option>
              <option value="ml">ml</option>
              <option value="l">l</option>
              <option value="cbm">cbm</option>
              <option value="qm">qm</option>
              <option value="piece">piece</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.TCFReferenceValueForCalculation?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="TCFQuantityOfMeasureForCalculation">
            <Form.Label>
              {intl.formatMessage({ id: 'quantity_of_measure' })}
              <span className="text-red-600 ml-1" aria-hidden="true">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              {...register('TCFQuantityOfMeasureForCalculation')}
              isInvalid={!!errors.TCFQuantityOfMeasureForCalculation}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.TCFQuantityOfMeasureForCalculation?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="TCFLiveCyclePhase">
            <Form.Label>{intl.formatMessage({ id: 'processes_for_greenhouse_gas_emission' })}</Form.Label>
            <Form.Select
              {...register('TCFProcessesForGreenhouseGasEmissionInATransportService')}
              isInvalid={!!errors.TCFProcessesForGreenhouseGasEmissionInATransportService}
              className="dpp-form-control"
            >
              <option value="WTT - Well-to-Tank">WTT - Well-to-Tank </option>
              <option value="TTW - Tank-to-Wheel">TTW - Tank-to-Wheel</option>
              <option value="WTW - Well-to-Wheel">WTW - Well-to-Wheel</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.TCFProcessesForGreenhouseGasEmissionInATransportService?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      {/* Address Handover */}
      <h5 className="mt-4">{intl.formatMessage({ id: 'goods_transport_address_handover' })}</h5>
      <Row className="mt-4">
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="TCFGoodsTransportAddressHandover.Street">
            <Form.Label>
              {intl.formatMessage({ id: 'street' })}
              <span className="text-red-600 ml-1" aria-hidden="true">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              {...register('TCFGoodsTransportAddressHandover.Street')}
              isInvalid={!!errors.TCFGoodsTransportAddressHandover?.Street}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.TCFGoodsTransportAddressHandover?.Street?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="TCFGoodsTransportAddressHandover.HouseNumber">
            <Form.Label>
              {intl.formatMessage({ id: 'house_number' })}
              <span className="text-red-600 ml-1" aria-hidden="true">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              {...register('TCFGoodsTransportAddressHandover.HouseNumber')}
              isInvalid={!!errors.TCFGoodsTransportAddressHandover?.HouseNumber}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.TCFGoodsTransportAddressHandover?.HouseNumber?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="TCFGoodsTransportAddressHandover.ZipCode">
            <Form.Label>
              {intl.formatMessage({ id: 'zip_code' })}
              <span className="text-red-600 ml-1" aria-hidden="true">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              {...register('TCFGoodsTransportAddressHandover.ZipCode')}
              isInvalid={!!errors.TCFGoodsTransportAddressHandover?.ZipCode}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.TCFGoodsTransportAddressHandover?.ZipCode?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="TCFGoodsTransportAddressHandover.CityTown">
            <Form.Label>
              {intl.formatMessage({ id: 'city_town' })}
              <span className="text-red-600 ml-1" aria-hidden="true">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              {...register('TCFGoodsTransportAddressHandover.CityTown')}
              isInvalid={!!errors.TCFGoodsTransportAddressHandover?.CityTown}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.TCFGoodsTransportAddressHandover?.CityTown?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="TCFGoodsTransportAddressHandover.Country">
            <Form.Label>
              {intl.formatMessage({ id: 'country' })}
              <span className="text-red-600 ml-1" aria-hidden="true">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              {...register('TCFGoodsTransportAddressHandover.Country')}
              isInvalid={!!errors.TCFGoodsTransportAddressHandover?.Country}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.TCFGoodsTransportAddressHandover?.Country?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={3}>
          <Form.Group className="mb-4" controlId="TCFGoodsTransportAddressHandover.Latitude">
            <Form.Label>{intl.formatMessage({ id: 'latitude' })}</Form.Label>
            <Form.Control
              type="number"
              step="0.000001"
              {...register('TCFGoodsTransportAddressHandover.Latitude')}
              isInvalid={!!errors.TCFGoodsTransportAddressHandover?.Latitude}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.TCFGoodsTransportAddressHandover?.Latitude?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={3}>
          <Form.Group className="mb-4" controlId="TCFGoodsTransportAddressHandover.Longitude">
            <Form.Label>{intl.formatMessage({ id: 'longitude' })}</Form.Label>
            <Form.Control
              type="number"
              step="0.000001"
              {...register('TCFGoodsTransportAddressHandover.Longitude')}
              isInvalid={!!errors.TCFGoodsTransportAddressHandover?.Longitude}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.TCFGoodsTransportAddressHandover?.Longitude?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      {/* Address Takeover */}
      <h5 className="mt-4">{intl.formatMessage({ id: 'goods_transport_address_takeover' })}</h5>
      <Row className="mt-4">
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="TCFGoodsTransportAddressTakeover.Street">
            <Form.Label>
              {intl.formatMessage({ id: 'street' })}
              <span className="text-red-600 ml-1" aria-hidden="true">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              {...register('TCFGoodsTransportAddressTakeover.Street')}
              isInvalid={!!errors.TCFGoodsTransportAddressTakeover?.Street}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.TCFGoodsTransportAddressTakeover?.Street?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="TCFGoodsTransportAddressTakeover.HouseNumber">
            <Form.Label>
              {intl.formatMessage({ id: 'house_number' })}
              <span className="text-red-600 ml-1" aria-hidden="true">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              {...register('TCFGoodsTransportAddressTakeover.HouseNumber')}
              isInvalid={!!errors.TCFGoodsTransportAddressTakeover?.HouseNumber}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.TCFGoodsTransportAddressTakeover?.HouseNumber?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="TCFGoodsTransportAddressTakeover.ZipCode">
            <Form.Label>
              {intl.formatMessage({ id: 'zip_code' })}
              <span className="text-red-600 ml-1" aria-hidden="true">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              {...register('TCFGoodsTransportAddressTakeover.ZipCode')}
              isInvalid={!!errors.TCFGoodsTransportAddressTakeover?.ZipCode}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.TCFGoodsTransportAddressTakeover?.ZipCode?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="TCFGoodsTransportAddressTakeover.CityTown">
            <Form.Label>
              {intl.formatMessage({ id: 'city_town' })}
              <span className="text-red-600 ml-1" aria-hidden="true">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              {...register('TCFGoodsTransportAddressTakeover.CityTown')}
              isInvalid={!!errors.TCFGoodsTransportAddressTakeover?.CityTown}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.TCFGoodsTransportAddressTakeover?.CityTown?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-4">
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="TCFGoodsTransportAddressTakeover.Country">
            <Form.Label>
              {intl.formatMessage({ id: 'country' })}
              <span className="text-red-600 ml-1" aria-hidden="true">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              {...register('TCFGoodsTransportAddressTakeover.Country')}
              isInvalid={!!errors.TCFGoodsTransportAddressTakeover?.Country}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.TCFGoodsTransportAddressTakeover?.Country?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={3}>
          <Form.Group className="mb-4" controlId="TCFGoodsTransportAddressTakeover.Latitude">
            <Form.Label>{intl.formatMessage({ id: 'latitude' })}</Form.Label>
            <Form.Control
              type="number"
              step="0.000001"
              {...register('TCFGoodsTransportAddressTakeover.Latitude')}
              isInvalid={!!errors.TCFGoodsTransportAddressTakeover?.Latitude}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.TCFGoodsTransportAddressTakeover?.Latitude?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={3}>
          <Form.Group className="mb-4" controlId="TCFGoodsTransportAddressTakeover.Longitude">
            <Form.Label>{intl.formatMessage({ id: 'longitude' })}</Form.Label>
            <Form.Control
              type="number"
              step="0.000001"
              {...register('TCFGoodsTransportAddressTakeover.Longitude')}
              isInvalid={!!errors.TCFGoodsTransportAddressTakeover?.Longitude}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.TCFGoodsTransportAddressTakeover?.Longitude?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      {/* Save / Cancel Buttons */}
      {showButtons && (
        <div className="d-flex justify-content-end gap-2">
          <Button
            variant="outline-secondary"
            onClick={handleCancel}
            className="dpp-modal-button"
          >
            {intl.formatMessage({ id: 'cancel' })}
          </Button>
          {isDemo ? (
            <OverlayTrigger
              placement="auto"
              overlay={<Tooltip id="save-tooltip">{noAccessMessage}</Tooltip>}
            >
              <span className="d-inline-block">
                <Button
                  variant="primary"
                  type="submit"
                  disabled
                  className="dpp-modal-button"
                  style={{ pointerEvents: 'none' }}
                >
                  {intl.formatMessage({ id: 'save' })}
                </Button>
              </span>
            </OverlayTrigger>
          ) : (
            <Button
              variant="primary"
              type="submit"
              disabled={!isDirty || !isValid}
              className="dpp-modal-button"
            >
              {intl.formatMessage({ id: 'save' })}
            </Button>
          )}
        </div>
      )}
    </Form>
  );
};

export default CO2ConsumptionsForm;